/**
 * Frontend Error Reporter
 * Captures and reports unhandled errors to the backend for automated issue creation.
 */

const API_URL = import.meta.env.VITE_API_URL || '';
const ERROR_REPORTING_ENABLED = import.meta.env.VITE_ERROR_REPORTING_ENABLED === 'true';

// Debounce map to prevent duplicate reports within short time window
const recentErrors = new Map<string, number>();
const DEBOUNCE_MS = 5000; // 5 seconds

// Error types to ignore (not bugs)
const IGNORED_ERRORS = [
  'ResizeObserver loop',
  'Script error.',
  'ChunkLoadError',
  'Loading chunk',
  'Network Error',
  'Failed to fetch',
  'AbortError',
  'cancelled',
  'The operation was aborted',
  'The user aborted a request',
  'InvalidStateError',
  'Failed to update a ServiceWorker',
];

interface ErrorReportPayload {
  errorType: string;
  message: string;
  stack?: string;
  sourceFile?: string;
  sourceLine?: number;
  sourceFunction?: string;
  classification: 'frontend_crash' | 'unhandled_rejection' | 'react_error_boundary';
  componentStack?: string;
  url?: string;
  userAgent?: string;
  context?: Record<string, unknown>;
}

/**
 * Generate a simple fingerprint for deduplication
 */
function generateFingerprint(error: ErrorReportPayload): string {
  const key = `${error.errorType}:${error.message}:${error.sourceFile || ''}`;
  return key.substring(0, 200);
}

/**
 * Check if error should be ignored
 */
function shouldIgnore(message: string): boolean {
  return IGNORED_ERRORS.some(pattern => message.includes(pattern));
}

/**
 * Check if error was recently reported (debounce)
 */
function isRecentlyReported(fingerprint: string): boolean {
  const lastReport = recentErrors.get(fingerprint);
  if (lastReport && Date.now() - lastReport < DEBOUNCE_MS) {
    return true;
  }
  recentErrors.set(fingerprint, Date.now());

  // Clean up old entries periodically
  if (recentErrors.size > 100) {
    const now = Date.now();
    for (const [key, time] of recentErrors) {
      if (now - time > DEBOUNCE_MS * 2) {
        recentErrors.delete(key);
      }
    }
  }

  return false;
}

/**
 * Extract source location from stack trace
 */
function extractSourceLocation(stack: string | undefined): {
  file?: string;
  line?: number;
  func?: string;
} {
  if (!stack) return {};

  const lines = stack.split('\n');
  for (const line of lines.slice(1)) {
    // Match: "at functionName (file.ts:123:45)" or "at file.ts:123:45"
    const match = line.match(/at\s+(?:(.+?)\s+\()?(.+?):(\d+)(?::\d+)?\)?/);
    if (match) {
      return {
        func: match[1]?.replace(/Object\.<anonymous>/, 'anonymous') || undefined,
        file: match[2],
        line: parseInt(match[3], 10),
      };
    }
  }
  return {};
}

/**
 * Send error report to backend
 * Uses sendBeacon for reliability during page unload
 */
function sendReport(payload: ErrorReportPayload): void {
  if (!ERROR_REPORTING_ENABLED) {
    console.debug('[ErrorReporter] Reporting disabled, not sending:', payload.message);
    return;
  }

  const fingerprint = generateFingerprint(payload);
  if (isRecentlyReported(fingerprint)) {
    console.debug('[ErrorReporter] Recently reported, skipping:', payload.message);
    return;
  }

  if (shouldIgnore(payload.message)) {
    console.debug('[ErrorReporter] Ignored error type:', payload.message);
    return;
  }

  const url = `${API_URL}/api/error-reports`;
  const body = JSON.stringify(payload);

  // Try sendBeacon first (more reliable during page unload)
  // Pass body as a plain string (not a JSON Blob) so the browser sends it as
  // text/plain — a CORS-safelisted content type that doesn't require preflight.
  // sendBeacon cannot handle preflight requests, so application/json would fail.
  if (navigator.sendBeacon) {
    const sent = navigator.sendBeacon(url, body);
    if (sent) {
      console.debug('[ErrorReporter] Sent via beacon:', payload.errorType);
      return;
    }
  }

  // Fall back to fetch
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body,
    keepalive: true,
  }).catch((err) => {
    // Silently fail - don't let error reporting cause more errors
    console.debug('[ErrorReporter] Failed to send report:', err);
  });
}

/**
 * Report an error with context
 */
export function reportError(
  error: Error,
  classification: ErrorReportPayload['classification'],
  context?: Record<string, unknown>
): void {
  const source = extractSourceLocation(error.stack);

  sendReport({
    errorType: error.name || 'Error',
    message: error.message || 'Unknown error',
    stack: error.stack,
    sourceFile: source.file,
    sourceLine: source.line,
    sourceFunction: source.func,
    classification,
    url: window.location.href,
    userAgent: navigator.userAgent,
    context,
  });
}

/**
 * Report a React ErrorBoundary error
 */
export function reportReactError(
  error: Error,
  componentStack?: string
): void {
  const source = extractSourceLocation(error.stack);

  sendReport({
    errorType: error.name || 'ReactError',
    message: error.message || 'Unknown React error',
    stack: error.stack,
    sourceFile: source.file,
    sourceLine: source.line,
    sourceFunction: source.func,
    classification: 'react_error_boundary',
    componentStack,
    url: window.location.href,
    userAgent: navigator.userAgent,
  });
}

/**
 * Global error handler for window.onerror
 */
function handleGlobalError(
  message: string | Event,
  source?: string,
  lineno?: number,
  colno?: number,
  error?: Error
): boolean {
  const errorMessage = typeof message === 'string' ? message : 'Unknown error';

  sendReport({
    errorType: error?.name || 'Error',
    message: error?.message || errorMessage,
    stack: error?.stack,
    sourceFile: source,
    sourceLine: lineno,
    classification: 'frontend_crash',
    url: window.location.href,
    userAgent: navigator.userAgent,
    context: {
      colno,
    },
  });

  // Return false to allow default error handling
  return false;
}

/**
 * Global handler for unhandled promise rejections
 */
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  const error = event.reason;
  const isError = error instanceof Error;

  sendReport({
    errorType: isError ? error.name : 'UnhandledRejection',
    message: isError ? error.message : String(error),
    stack: isError ? error.stack : undefined,
    classification: 'unhandled_rejection',
    url: window.location.href,
    userAgent: navigator.userAgent,
    context: {
      promiseReason: !isError ? String(error) : undefined,
    },
  });
}

/**
 * Initialize global error handlers
 * Call this once at app startup
 */
export function initializeErrorReporter(): void {
  if (!ERROR_REPORTING_ENABLED) {
    console.debug('[ErrorReporter] Error reporting is disabled');
    return;
  }

  // Set up global error handler
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    handleGlobalError(message, source, lineno, colno, error);
    if (originalOnError) {
      return originalOnError.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  // Set up unhandled rejection handler
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  console.debug('[ErrorReporter] Initialized global error handlers');
}

/**
 * Error reporter singleton
 */
export const errorReporter = {
  report: reportError,
  reportReactError,
  initialize: initializeErrorReporter,
};

export default errorReporter;
